import { useState } from 'react';
import {
  Plane,
  Hotel,
  MapPin,
  Car,
  Utensils,
  GripVertical,
  Edit2,
  Trash2,
  Clock,
  DollarSign,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ItineraryItem as ItineraryItemType, ItemType } from '../types/itinerary';
import { formatDuration } from '../lib/scheduler';

interface ItineraryItemProps {
  item: ItineraryItemType;
  onEdit?: (item: ItineraryItemType) => void;
  onDelete?: (itemId: string) => void;
}

const typeIcons: Record<ItemType, React.ComponentType<{ className?: string }>> = {
  flight: Plane,
  hotel: Hotel,
  activity: MapPin,
  restaurant: Utensils, // Reuse meal icon for restaurant
  transport: Car,
  other: MapPin, // Reuse activity icon for other
  meal: Utensils,
};

const statusColors = {
  planned: 'bg-gray-100 text-gray-700',
  booked: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

const typeColors: Record<ItemType, string> = {
  flight: 'border-l-blue-500 bg-blue-50',
  hotel: 'border-l-purple-500 bg-purple-50',
  activity: 'border-l-green-500 bg-green-50',
  restaurant: 'border-l-red-500 bg-red-50', // Reuse meal colors
  transport: 'border-l-orange-500 bg-orange-50',
  other: 'border-l-gray-500 bg-gray-50', // Neutral color for other
  meal: 'border-l-red-500 bg-red-50',
};

export default function ItineraryItem({ item, onEdit, onDelete }: ItineraryItemProps) {
  const [showActions, setShowActions] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const TypeIcon = typeIcons[item.type || item.item_type] || MapPin;

  const formatTime = (dateString: string) => {
    if (!dateString) return '--:--';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '--:--';
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative group flex items-start gap-3 p-4 rounded-xl border border-gray-200 border-l-4 ${typeColors[item.type || item.item_type]} transition-all hover:shadow-md ${
        isDragging ? 'shadow-lg ring-2 ring-primary-500 cursor-grabbing' : ''
      }`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Drag Handle */}
      <div
        className={`cursor-grab active:cursor-grabbing mt-1 opacity-0 group-hover:opacity-100 transition-opacity ${
          showActions ? 'opacity-100' : ''
        }`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-5 w-5 text-gray-400" />
      </div>

      {/* Type Icon */}
      <div
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          item.type === 'flight'
            ? 'bg-blue-100 text-blue-600'
            : item.type === 'hotel'
            ? 'bg-purple-100 text-purple-600'
            : item.type === 'activity'
            ? 'bg-green-100 text-green-600'
            : item.type === 'transport'
            ? 'bg-orange-100 text-orange-600'
            : 'bg-red-100 text-red-600'
        }`}
      >
        <TypeIcon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h4 className="font-semibold text-gray-900 truncate">{item.title}</h4>
            {item.location && (
              <p className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">
                  {item.location.name || item.location.address}
                </span>
              </p>
            )}
          </div>

          {/* Status Badge */}
          <span
            className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
              statusColors[item.status]
            }`}
          >
            {item.status}
          </span>
        </div>

        {/* Time and Duration */}
        <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>
              {formatTime(item.startTime)} - {formatTime(item.endTime)}
            </span>
          </div>
          <span className="text-gray-300">|</span>
          <span>{formatDuration(item.duration)}</span>
        </div>

        {/* Description/Notes */}
        {item.description && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{item.description}</p>
        )}

        {/* Cost */}
        <div className="flex items-center gap-1 mt-2 text-sm font-medium">
          <DollarSign className="h-4 w-4 text-gray-400" />
          <span>
            {item.currency} {(item.cost || 0).toLocaleString()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div
        className={`absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity ${
          showActions ? 'opacity-100' : ''
        }`}
      >
        {onEdit && (
          <button
            onClick={() => onEdit(item)}
            className="p-1.5 hover:bg-gray-200 rounded-lg transition"
            title="Edit"
          >
            <Edit2 className="h-4 w-4 text-gray-500" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(item.id)}
            className="p-1.5 hover:bg-red-100 rounded-lg transition"
            title="Delete"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </button>
        )}
      </div>
    </div>
  );
}
